import type { FieldArrayRenderProps } from 'formik';
import type { ActivityDto } from 'shared/dtos/activities.dto';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import { confirmAlert } from 'react-confirm-alert';

import { usePermissions } from '../../../../contexts/permissions.context';
import { vocabulary } from '../../../../vocabulary/german';
import { SelectField } from '../../../fields/select-field/select-field';
import { TextareaField } from '../../../fields/textarea-field/textarea-field';
import { ConfirmationModal } from '../../../template/modals/confirmation-modal';

export interface DivisionFormActivitiesItemProps {
  index: number;
  currentYearFrozen: boolean;
  activitiesHelper: FieldArrayRenderProps;
  componentName: string;
  activity: ActivityDto;
}

export const DivisionFormActivitiesItem = ({
  index,
  currentYearFrozen,
  activitiesHelper,
  componentName,
  activity,
}: DivisionFormActivitiesItemProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: activity.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : 'transform 500ms ease',
  };
  const matrix = usePermissions();

  const canRemove = currentYearFrozen
    ? matrix.permissions.freezeRemoveFieldsInside === true
    : matrix.permissions.removeFieldsInside === true;
  const canDrag = matrix.permissions.dragFields === true;
  const showControls = canRemove || canDrag;

  return (
    <div
      className="activity-item"
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <div className="row">
        <div className="activity-col">
          <TextareaField
            className={'textarea-field_min_height_reduced'}
            name={`Activities.${index}.title`}
            placeholder={
              vocabulary.forms.activities.placeholders.activityDescription
            }
            forDivision
            currentYearFrozen={currentYearFrozen}
          />
        </div>
        <div className="activity-col">
          <TextareaField
            currentYearFrozen={currentYearFrozen}
            className={'textarea-field_min_height_reduced'}
            name={`Activities.${index}.beforeLastYear`}
            forDivision
          />
        </div>
        <div className="activity-col">
          <TextareaField
            currentYearFrozen={currentYearFrozen}
            className={'textarea-field_min_height_reduced'}
            name={`Activities.${index}.lastYear`}
            forDivision
          />
        </div>
        <div className="activity-col">
          <TextareaField
            currentYearFrozen={currentYearFrozen}
            className={'textarea-field_min_height_reduced'}
            name={`Activities.${index}.curYear`}
            forDivision
          />
        </div>
        <div className="activity-col">
          <SelectField
            name={`Activities.${index}.controllable`}
            forDivision
            value={activity.controllable}
          >
            <option value={Number('0')} label="Nein" />
            <option value={Number('1')} label="Ja" />
          </SelectField>
          {showControls && (
            <div className="activity-col__controls">
              {canRemove && (
                <i
                  data-controlled-permissions={componentName}
                  className={'priorities__remove-icon'}
                  onClick={() => {
                    confirmAlert({
                      customUI: ({ onClose }) => {
                        return (
                          <ConfirmationModal
                            forDivision
                            onClose={onClose}
                            onOk={() => activitiesHelper.remove(index)}
                          />
                        );
                      },
                    });
                  }}
                />
              )}
              {canDrag && (
                <span
                  className="activities__sort-handle"
                  {...listeners}
                  style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    userSelect: 'none',
                  }}
                >
                  ::
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
